import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
    ChevronLeft, User as UserIcon, Calendar, ClipboardList, Sparkles, Download,
    Heart, Activity, TrendingUp, MessageSquare, Pill, Briefcase, UploadCloud, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KioskVitalsReport } from "../components/KioskVitalsReport";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

/**
 * Admin full-page detail view for a single patient.
 * Route: /admin/patients/:id
 */
export default function AdminPatientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    useAuth();
    const [patient, setPatient] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [medical, setMedical] = useState(null);
    const [tab, setTab] = useState("latest");
    const [loading, setLoading] = useState(true);
    const [showAdminUpload, setShowAdminUpload] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [p, s] = await Promise.all([
                    api.get(`/admin/patients/${id}`),
                    api.get(`/admin/patients/${id}/sessions`),
                ]);
                if (cancelled) return;
                setPatient(p.data);
                setSessions(s.data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Could not load patient");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    // Medical records aggregate loaded lazily
    const loadMedical = async () => {
        if (medical) return;
        try {
            const { data } = await api.get(`/admin/patients/${id}/medical-records`);
            setMedical(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load medical records");
        }
    };

    const latestVitals = sessions[0]?.vitals;
    const trend = useMemo(() => {
        return [...sessions].reverse().map((s) => ({
            date: new Date(s.vitals.captured_at).toLocaleDateString(),
            score: s.vitals.health_score,
            bmi: s.vitals.bmi,
            systolic: s.vitals.blood_pressure_systolic,
            hr: s.vitals.heart_rate,
        }));
    }, [sessions]);

    if (loading) return <div className="min-h-screen grid place-items-center text-gray-500">Loading patient…</div>;
    if (!patient) return null;

    return (
        <div className="min-h-screen bg-[var(--medx-cream)]" data-testid="admin-patient-detail">
            {/* Sticky top bar */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
                    <button onClick={() => navigate("/admin#patients")} className="text-sm text-gray-500 hover:text-[var(--medx-red)] inline-flex items-center gap-1" data-testid="admin-patient-back">
                        <ChevronLeft className="w-4 h-4" /> Back to Patients
                    </button>
                    <Link to="/admin" className="font-display text-lg font-bold">medx<span className="text-[var(--medx-orange)]">.</span>ai</Link>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 space-y-6">
                {/* Patient header */}
                <div className="card-soft p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white text-3xl font-display font-bold shrink-0">
                        {(patient.name || "").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono tracking-[0.22em] text-[var(--medx-red)]">PATIENT FILE</div>
                        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight" data-testid="admin-patient-name">{patient.name}</h1>
                        <div className="text-sm text-gray-500 flex flex-wrap items-center gap-3 mt-1">
                            <span>{patient.email}</span>
                            {patient.age && <span>· {patient.age} yr</span>}
                            {patient.gender && <span>· {patient.gender}</span>}
                            {patient.country && <span>· {patient.country}</span>}
                            {patient.employer_name && <span>· 🏢 {patient.employer_name}</span>}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs font-mono text-gray-500">HEALTH SCORE</div>
                        <div className="font-display text-5xl font-bold brand-gradient-text" data-testid="admin-patient-score">{patient.health_score ?? "—"}</div>
                        <div className="text-xs text-gray-400 mt-1">{sessions.length} scans · Joined {new Date(patient.created_at).toLocaleDateString()}</div>
                        <button
                            onClick={() => setShowAdminUpload(true)}
                            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--medx-orange)] text-white text-xs font-semibold hover:bg-[var(--medx-red)] transition shadow"
                            data-testid="admin-upload-scan-btn"
                        >
                            <UploadCloud className="w-3.5 h-3.5" /> Upload scan for patient
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-2 flex-wrap">
                    {[
                        { k: "latest", label: "Latest Kiosk Report", icon: ClipboardList },
                        { k: "history", label: `Timeline (${sessions.length})`, icon: TrendingUp },
                        { k: "records", label: "Medical Records", icon: MessageSquare },
                    ].map((tt) => {
                        const Icon = tt.icon;
                        const active = tab === tt.k;
                        return (
                            <button
                                key={tt.k}
                                onClick={() => { setTab(tt.k); if (tt.k === "records") loadMedical(); }}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                                    active ? "bg-[var(--medx-red)] text-white border-[var(--medx-red)]" : "bg-white text-gray-700 border-gray-200 hover:border-[var(--medx-orange)]"
                                }`}
                                data-testid={`admin-patient-tab-${tt.k}`}
                            >
                                <Icon className="w-4 h-4" /> {tt.label}
                            </button>
                        );
                    })}
                </div>

                {tab === "latest" && (
                    latestVitals ? (
                        <KioskVitalsReport vitals={latestVitals} patient={patient} />
                    ) : (
                        <div className="card-soft p-10 text-center text-sm text-gray-500">This patient has no kiosk scans yet.</div>
                    )
                )}

                {tab === "history" && (
                    sessions.length === 0 ? (
                        <div className="card-soft p-10 text-center text-sm text-gray-500">No sessions to display.</div>
                    ) : (
                        <div className="space-y-4">
                            <div className="card-soft p-6" data-testid="admin-patient-trend">
                                <div className="text-xs font-mono tracking-wider text-gray-500 mb-3">HEALTH SCORE OVER TIME</div>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={trend}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                            <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} />
                                            <YAxis stroke="#9ca3af" fontSize={11} />
                                            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #f3f4f6" }} />
                                            <Legend />
                                            <Line type="monotone" dataKey="score" stroke="#e63946" strokeWidth={2.5} name="Score" />
                                            <Line type="monotone" dataKey="systolic" stroke="#f77f00" strokeWidth={2} name="Systolic BP" />
                                            <Line type="monotone" dataKey="hr" stroke="#0ea5e9" strokeWidth={2} name="Heart Rate" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div className="card-soft overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 text-gray-500 text-xs font-mono tracking-wider">
                                        <tr>
                                            <th className="text-left p-4">DATE</th>
                                            <th className="text-left p-4">KIOSK</th>
                                            <th className="text-left p-4">BP</th>
                                            <th className="text-left p-4">HR</th>
                                            <th className="text-left p-4">SpO₂</th>
                                            <th className="text-left p-4">BMI</th>
                                            <th className="text-left p-4">FLAG</th>
                                            <th className="text-left p-4">SCORE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sessions.map((s, i) => (
                                            <tr key={i} className="border-t border-gray-100 hover:bg-orange-50/40" data-testid={`admin-session-${i}`}>
                                                <td className="p-4 text-gray-700">{new Date(s.vitals.captured_at).toLocaleString()}</td>
                                                <td className="p-4 font-mono text-xs text-gray-500">{s.vitals.kiosk_id}</td>
                                                <td className="p-4">{s.vitals.blood_pressure_systolic}/{s.vitals.blood_pressure_diastolic}</td>
                                                <td className="p-4">{s.vitals.heart_rate}</td>
                                                <td className="p-4">{s.vitals.spo2}%</td>
                                                <td className="p-4">{s.vitals.bmi}</td>
                                                <td className="p-4">
                                                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${s.vitals.hr_flag?.code === "urgent_leave" ? "bg-red-100 text-red-700" : s.vitals.hr_flag?.code === "take_a_break" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                                        {s.vitals.hr_flag?.label || "—"}
                                                    </span>
                                                </td>
                                                <td className="p-4 font-bold text-[var(--medx-red)]">{s.vitals.health_score}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )
                )}

                {tab === "records" && (
                    !medical ? (
                        <div className="card-soft p-10 text-center text-sm text-gray-500">Loading medical records…</div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="card-soft p-5">
                                <div className="text-xs font-mono tracking-wider text-gray-500 mb-3 flex items-center gap-1"><Pill className="w-3 h-3" /> PRESCRIPTIONS ({medical.prescriptions.length})</div>
                                {medical.prescriptions.length === 0 ? <div className="text-sm text-gray-400">None</div> : (
                                    <ul className="space-y-2">
                                        {medical.prescriptions.slice(0, 8).map((rx) => (
                                            <li key={rx.id} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                                                <strong>{rx.label}</strong> · <span className="text-gray-500">{rx.doctor_name}</span>
                                                <div className="text-[11px] text-gray-400">{new Date(rx.created_at).toLocaleString()}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="card-soft p-5">
                                <div className="text-xs font-mono tracking-wider text-gray-500 mb-3 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> DOCTOR NOTES ({medical.doctor_notes.length})</div>
                                {medical.doctor_notes.length === 0 ? <div className="text-sm text-gray-400">None</div> : (
                                    <ul className="space-y-2">
                                        {medical.doctor_notes.slice(0, 8).map((n) => (
                                            <li key={n.id} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                                                <div className="text-gray-800 whitespace-pre-wrap line-clamp-3">{n.note}</div>
                                                <div className="text-[11px] text-gray-400">{n.doctor_name} · {new Date(n.created_at).toLocaleString()}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="card-soft p-5">
                                <div className="text-xs font-mono tracking-wider text-gray-500 mb-3 flex items-center gap-1"><Briefcase className="w-3 h-3" /> EMPLOYER REMARKS ({medical.employer_remarks.length})</div>
                                {medical.employer_remarks.length === 0 ? <div className="text-sm text-gray-400">None</div> : (
                                    <ul className="space-y-2">
                                        {medical.employer_remarks.map((r) => (
                                            <li key={r.id} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                                                <strong>{r.employer_name}</strong>: {r.remark}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="card-soft p-5">
                                <div className="text-xs font-mono tracking-wider text-gray-500 mb-3 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI VALUATION REPORTS ({medical.ai_reports.length})</div>
                                {medical.ai_reports.length === 0 ? <div className="text-sm text-gray-400">None</div> : (
                                    <ul className="space-y-2">
                                        {medical.ai_reports.slice(0, 3).map((r) => (
                                            <li key={r.id} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                                                <div className="text-gray-800 line-clamp-3">{r.report.summary}</div>
                                                <div className="text-[11px] text-gray-400">{new Date(r.created_at).toLocaleString()}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )
                )}
            </main>
            {showAdminUpload && (
                <AdminScanUploadModal
                    patient={patient}
                    onClose={() => setShowAdminUpload(false)}
                    onUploaded={async () => {
                        setShowAdminUpload(false);
                        // refresh sessions + patient to show new score
                        const [p, s] = await Promise.all([
                            api.get(`/admin/patients/${id}`),
                            api.get(`/admin/patients/${id}/sessions`),
                        ]);
                        setPatient(p.data);
                        setSessions(s.data || []);
                    }}
                />
            )}
        </div>
    );
}

function AdminScanUploadModal({ patient, onClose, onUploaded }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notes, setNotes] = useState("");

    const onFile = (f) => {
        if (!f) return;
        const mime = (f.type || "").toLowerCase();
        if (mime !== "image/png" && mime !== "image/jpeg" && mime !== "image/jpg") {
            toast.error("Only PNG or JPEG assessment slips are accepted.");
            return;
        }
        if (f.size > 8_000_000) { toast.error("Please pick an image under 8 MB"); return; }
        setFile(f);
        const r = new FileReader();
        r.onload = () => setPreview(r.result);
        r.readAsDataURL(f);
    };

    const submit = async () => {
        if (!preview || !patient?.id) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/patients/${patient.id}/scans/upload`, {
                image_base64: preview,
                mime_type: file?.type || "image/jpeg",
                notes: notes || undefined,
            });
            toast.success(data.message || "Scan uploaded.", { duration: 6000 });
            onUploaded?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload failed", { duration: 7000 });
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="admin-scan-upload-modal">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <div>
                        <h3 className="font-display text-xl font-semibold">Upload slip for {patient?.name}</h3>
                        <p className="text-xs text-gray-500 mt-1">AI will validate the Medx AI markers and auto-create a session on this patient's dashboard. PNG or JPEG only.</p>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <label htmlFor="admin-scan-file" className="block cursor-pointer border-2 border-dashed border-gray-200 rounded-2xl p-6 hover:border-[var(--medx-orange)] hover:bg-orange-50/30 text-center" data-testid="admin-scan-upload-dropzone">
                        {preview ? (
                            <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                        ) : (
                            <>
                                <UploadCloud className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                                <div className="font-medium text-gray-700">Click to pick a Medx AI slip image</div>
                                <div className="text-xs text-gray-500 mt-1">PNG or JPEG only · max 8 MB</div>
                            </>
                        )}
                        <input id="admin-scan-file" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                            className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="admin-scan-upload-input" />
                    </label>
                    <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        placeholder="Admin note (optional)"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none"
                        data-testid="admin-scan-upload-notes" />
                    <div className="flex justify-end gap-2 pt-1">
                        <button onClick={onClose} className="btn-outline text-sm" disabled={busy}>Cancel</button>
                        <button onClick={submit} disabled={busy || !preview} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="admin-scan-upload-submit">
                            <UploadCloud className="w-4 h-4" /> {busy ? "Scanning & analysing…" : "Upload & analyse"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
